import { CronExpressionParser } from "cron-parser";

export type CronValidationResult = "valid" | "invalid" | "tooFrequent";

const NAMED_CRON_VALUES = /JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|SUN|MON|TUE|WED|THU|FRI|SAT/gi;

export function validateCronExpression(expression: string): CronValidationResult {
    if (typeof expression !== "string") {
        return "invalid";
    }

    const trimmed = expression.trim();
    if (!trimmed) {
        return "invalid";
    }

    const parts = trimmed.split(/\s+/);
    if (parts.length !== 5 && parts.length !== 6) {
        return "invalid";
    }

    if (parts.some(hasUnsupportedCronSyntax) || dayOfWeekOutOfRange(parts[parts.length - 1])) {
        return "invalid";
    }

    try {
        CronExpressionParser.parse(trimmed);
    } catch {
        return "invalid";
    }

    const includeSeconds = parts.length === 6;
    const tooFrequent = (includeSeconds && !trimmed.startsWith("0 0 ")) || !trimmed.startsWith("0 ");
    if (tooFrequent) {
        return "tooFrequent";
    }

    return "valid";
}

export function isValidCronExpression(expression: string): boolean {
    return validateCronExpression(expression) === "valid";
}

export function getCronValidationMessage(expression: string): string | null {
    const result = validateCronExpression(expression);
    if (result === "invalid") {
        return "Invalid cron expression";
    }
    if (result === "tooFrequent") {
        return "Job execution frequency must be at least one hour";
    }
    return null;
}

function hasUnsupportedCronSyntax(part: string): boolean {
    if (/[?#]/.test(part)) {
        return true;
    }

    const withoutNames = part.replace(NAMED_CRON_VALUES, "");
    return /[LWH]/i.test(withoutNames);
}

function dayOfWeekOutOfRange(part: string): boolean {
    const withoutNames = part.replace(/SUN|MON|TUE|WED|THU|FRI|SAT/gi, "");
    const withoutSteps = withoutNames.replace(/\/\d+/g, "");
    const numbers = withoutSteps.match(/\d+/g) ?? [];
    return numbers.some((value) => Number(value) > 6);
}
